import { x25519 } from '@noble/curves/ed25519';
import { hkdf } from '@noble/hashes/hkdf';
import { sha256 } from '@noble/hashes/sha2';
import { xchacha20poly1305 } from '@noble/ciphers/chacha';
import { concat, utf8 } from './bytes.js';

// HKDF info string: doubles as a domain separator and a wrap-format version tag.
// v2 (current) binds the wrap key to (ephem_pub || recipient_pub) in the salt; a
// v1 wrapper and a v2 unwrapper would derive different keys, so any cross-version
// attempt fails the AEAD tag immediately rather than producing garbage plaintext.
const HKDF_INFO = utf8('timelock-wrap-v2');

/**
 * Wrap a 32-byte content-encryption key to an X25519 recipient public key.
 *
 * Construction: HKDF-SHA256 over the X25519 shared secret, with
 *   salt = ephem_pub || recipient_pub
 *   info = "timelock-wrap-v2"
 * yielding a 32-byte XChaCha20-Poly1305 key. recipient_pub is included in the
 * salt to bind the derived key to the intended recipient; this matches age's
 * X25519 stanza derivation and protects against an entire class of curve foot-
 * guns where a single shared secret might otherwise be reachable by multiple
 * recipient keys.
 *
 * Wire format: ephemeral_pub (32) || nonce (24) || ciphertext (cek + 16B tag).
 * recipient_pub is not carried because the unwrapper holds recipient_priv and
 * can derive it locally.
 */
export function wrapX25519(cek: Uint8Array, recipientPub: Uint8Array): Uint8Array {
  if (cek.length !== 32) throw new Error('CEK must be 32 bytes');
  if (recipientPub.length !== 32) throw new Error('recipient pubkey must be 32 bytes');

  const ephemPriv = crypto.getRandomValues(new Uint8Array(32));
  const ephemPub = x25519.getPublicKey(ephemPriv);
  const shared = x25519.getSharedSecret(ephemPriv, recipientPub);
  const salt = concat(ephemPub, recipientPub);
  const key = hkdf(sha256, shared, salt, HKDF_INFO, 32);
  const nonce = crypto.getRandomValues(new Uint8Array(24));
  const aead = xchacha20poly1305(key, nonce);
  const ct = aead.encrypt(cek);
  return concat(ephemPub, nonce, ct);
}

export function unwrapX25519(wrapped: Uint8Array, recipientPriv: Uint8Array): Uint8Array | null {
  if (wrapped.length < 32 + 24 + 16 + 32) return null;
  if (recipientPriv.length !== 32) return null;
  const ephemPub = wrapped.subarray(0, 32);
  const nonce = wrapped.subarray(32, 56);
  const ct = wrapped.subarray(56);
  try {
    const shared = x25519.getSharedSecret(recipientPriv, ephemPub);
    // recipient_pub is derivable from recipient_priv — keep wire format unchanged.
    const recipientPub = x25519.getPublicKey(recipientPriv);
    const salt = concat(ephemPub, recipientPub);
    const key = hkdf(sha256, shared, salt, HKDF_INFO, 32);
    const aead = xchacha20poly1305(key, nonce);
    return aead.decrypt(ct);
  } catch {
    return null;
  }
}

export function generateX25519Keypair(): { privateKey: Uint8Array; publicKey: Uint8Array } {
  const privateKey = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}
